import type { ApprovalStage, ApprovalStateDto, RouteStepDto } from '@process-hub/shared';
import { useState } from 'react';
import { formatWhen } from '../time';

/**
 * Where a version is in approval, who decided what, and — for the pending stage's decider —
 * the decision controls. BUILD.md §9, D-023. Comments are required for anything but Approve.
 */

export const STAGE_LABEL: Record<ApprovalStage, string> = {
  OwnerReview: 'Owner review',
  PeerValidation: 'Peer / SME validation',
  DeptHeadSignoff: 'Department head sign-off',
  ComplianceGate: 'Compliance gate',
};

function decider(step: RouteStepDto): string {
  if (step.mode === 'Owner') return 'the process owner';
  if (step.adminFallback) return 'an Admin (the routed approver owns this version)';
  if (step.reroutedFromOwner) return 'the department alternate (the head owns this version)';
  switch (step.mode) {
    case 'DeptHead':
      return 'the department head';
    case 'NamedGroup':
      return 'the named approver group';
    default:
      return 'any Approver in the department';
  }
}

export function ApprovalPanel({ state, onDecide, busy }: { state: ApprovalStateDto; onDecide: (decision: 'Approved' | 'ChangesRequested' | 'Rejected', comment: string) => void; busy: boolean }) {
  const [comment, setComment] = useState('');
  const inApproval = Boolean(state.submittedAt) && !state.approvedAt;
  const since = state.submittedAt ? new Date(state.submittedAt) : null;
  const thisRound = state.decisions.filter((d) => since && new Date(d.decidedAt) >= since);
  const earlier = state.decisions.filter((d) => !since || new Date(d.decidedAt) < since);

  return (
    <div className="approval">
      <div className="approval__head">
        {state.approvedAt ? (
          <span>
            Approved {formatWhen(state.approvedAt)}. This version is final and cannot change.
          </span>
        ) : inApproval ? (
          <span>
            In approval since {formatWhen(state.submittedAt!)}
            {state.pending ? ` — waiting on ${STAGE_LABEL[state.pending.stage].toLowerCase()} (${decider(state.pending)})` : ''}.
          </span>
        ) : (
          <span className="muted">Not submitted.</span>
        )}
      </div>

      {state.route.length > 0 && (
        <ol className="approval__route">
          {state.route.map((step) => {
            const decision = thisRound.find((d) => d.stage === step.stage && d.decision === 'Approved');
            const isPending = state.pending?.stage === step.stage;
            return (
              <li key={step.stage} className={`approval__stage${decision ? ' approval__stage--done' : isPending ? ' approval__stage--pending' : ''}`}>
                <span className="approval__mark" aria-hidden="true">
                  {decision ? '✓' : isPending ? '●' : '○'}
                </span>
                <span>
                  <strong>{STAGE_LABEL[step.stage]}</strong>
                  <span className="muted"> · {decider(step)}</span>
                  {decision && (
                    <div className="grid__secondary">
                      Approved by {decision.approverName}, {formatWhen(decision.decidedAt)}
                      {decision.comment ? ` — “${decision.comment}”` : ''}
                    </div>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {state.canDecide && state.pending && (
        <div className="approval__decide">
          <label className="field">
            <span>Comment (required unless approving)</span>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={2000} placeholder="What should change, or why this is rejected." />
          </label>
          <div className="approval__buttons">
            <button type="button" className="button" disabled={busy} onClick={() => onDecide('Approved', comment)}>
              Approve {STAGE_LABEL[state.pending.stage].toLowerCase()}
            </button>
            <button type="button" className="button button--secondary" disabled={busy || !comment.trim()} onClick={() => onDecide('ChangesRequested', comment)}>
              Request changes
            </button>
            <button type="button" className="button button--secondary button--danger" disabled={busy || !comment.trim()} onClick={() => onDecide('Rejected', comment)}>
              Reject
            </button>
          </div>
        </div>
      )}

      {earlier.length > 0 && (
        <details className="approval__history">
          <summary>Earlier rounds ({earlier.length})</summary>
          <ul>
            {earlier.map((d) => (
              <li key={d.id}>
                {formatWhen(d.decidedAt)} — {STAGE_LABEL[d.stage]}: <strong>{d.decision}</strong> by {d.approverName}
                {d.comment ? ` — “${d.comment}”` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
